import "dayjs/locale/ar"; // Arabic
import "dayjs/locale/zh-cn"; // Chinese
import "dayjs/locale/de"; // German
import "dayjs/locale/en-gb"; // English
import "dayjs/locale/es"; // Spanish
import "dayjs/locale/fr"; // French
import "dayjs/locale/ka"; // Georgian
import "dayjs/locale/el"; // Greek
import "dayjs/locale/hi"; // Hindi
import "dayjs/locale/he"; // Hebrew
import "dayjs/locale/fa"; // Persian
import "dayjs/locale/it"; // Italian
import "dayjs/locale/ja"; // Japanese
import "dayjs/locale/ko"; // Korean
import "dayjs/locale/pl"; // Polish
import "dayjs/locale/pt"; // Portuguese
import "dayjs/locale/ru"; // Russian
import "dayjs/locale/th"; // Thai
import "dayjs/locale/tr"; // Turkish
import "dayjs/locale/uk"; // Ukrainian
import "dayjs/locale/vi"; // Vietnamese
import dayjs, { ConfigType, Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localeData from "dayjs/plugin/localeData";
import utc from "dayjs/plugin/utc";
import { LanguagesMapType } from "shared/localization/languages";
import { localization } from "shared/localization/localization";

dayjs.extend(customParseFormat);
dayjs.extend(localeData);
dayjs.extend(utc);

type LanguagesMapKeys = keyof LanguagesMapType;

export const locales: Record<LanguagesMapKeys, string> = {
  ar: "ar",
  cn: "zh-cn",
  de: "de",
  en: "en-gb",
  es: "es",
  fr: "fr",
  ge: "ka", // Georgian (unofficial support via dayjs)
  gr: "el", // Greek
  hi: "hi",
  il: "he", // Hebrew
  ir: "fa", // Persian
  it: "it",
  jp: "ja",
  kr: "ko",
  pl: "pl",
  pt: "pt",
  ru: "ru",
  th: "th",
  tr: "tr",
  ua: "uk",
  vi: "vi",
};

export enum Time {
  Millisecond = 1,
  Second = 1000,
  Minute = 60 * Time.Second,
  Hour = 60 * Time.Minute,
  Day = 24 * Time.Hour,
  Week = 7 * Time.Day,
}

export enum DateFormatList {
  FULL = "YYYY-MM-DD HH:mm:ss",
  CARD_BASE = "D MMM YYYY",
  DATE_TIME_AM = "D MMM YYYY h:mm A",
  CARD_WITH_TIME_12H = "D MMM YYYY hh:mm a",
  CARD_WITH_TIME_24H = "D MMM YYYY HH:mm",
  YEAR_MONTH_DAY = "YYYY-MM-DD",
  MONTH_YEAR = "MMMM YYYY",
  DAY_MONTH = "DD MMMM",
  MONTH = "MMMM",
}

export type FormatCardDateOptions = {
  withTime?: boolean;
  format?: "12h" | "24h";
};

export type AnyDate = NonNullable<ConfigType>;
export const formatCardDate = (
  date: AnyDate,
  { withTime = false, format = "12h" }: FormatCardDateOptions = {}
): string => {
  const lang = localization.language as LanguagesMapKeys;

  if (withTime) {
    return format === "12h"
      ? dayjs(date).locale(locales[lang]).format(DateFormatList.CARD_WITH_TIME_12H)
      : dayjs(date).locale(locales[lang]).format(DateFormatList.CARD_WITH_TIME_24H);
  }

  return dayjs(date).locale(locales[lang]).format(DateFormatList.CARD_BASE);
};

export const formatApiDate = (date: AnyDate, { utc = true }: { utc?: boolean } = {}): string => {
  if (utc) {
    return dayjs(date).utc().format(DateFormatList.FULL);
  }
  return dayjs(date).format(DateFormatList.FULL);
};

export const getUtcTimestamp = (date: AnyDate): number => {
  return dayjs(date).utc().valueOf();
};

export const formatDate = (
  date: AnyDate,
  { format = DateFormatList.FULL }: { format?: DateFormatList } = {}
): string => {
  const lang = localization.language as LanguagesMapKeys;
  return dayjs(date).locale(locales[lang]).format(format);
};

export const formatFollowCardDate = (date: AnyDate): string => {
  return dayjs(date).locale(locales.en).format(DateFormatList.CARD_BASE);
};

export const shiftDateByDay = (date: AnyDate, operation: "add" | "subtract"): Dayjs => {
  const dateObj = dayjs(date);
  return operation === "add" ? dateObj.add(1, "day") : dateObj.subtract(1, "day");
};

export const todayDate = (): string => {
  return dayjs().format(DateFormatList.YEAR_MONTH_DAY);
};
